<script>
import { mapGetters } from 'vuex';
import CsatMetricCard from './ReportMetricCard.vue';
import ScaleDistributionBar from 'dashboard/components-next/csat/ScaleDistributionBar.vue';
import { formatCsatScore } from 'dashboard/helper/csatScore';
import { formatMetricWithCount } from 'dashboard/helper/reportMetricHelper';

// A nota e a barra saem POR ESCALA (Csat::ScoreSummary.by_scale): 1 a 5 é o
// CSAT de sempre (% de 4-5 e carinhas); outra escala usa a média na régua dela.
// Período com mais de uma escala mostra uma linha por escala — somar 3-de-5
// com 3-de-10 inventaria a nota (e a barra antiga contava um 3 de 10 como 😐).
export default {
  components: { CsatMetricCard, ScaleDistributionBar },
  props: {
    filters: {
      type: Object,
      required: true,
    },
  },
  computed: {
    ...mapGetters({
      metrics: 'csat/getMetrics',
    }),
    ratingFilterEnabled() {
      return Boolean(this.filters.rating);
    },
    scales() {
      return this.metrics.scales || [];
    },
    singleScale() {
      return this.scales.length === 1 ? this.scales[0] : null;
    },
    hasMixedScales() {
      return this.scales.length > 1;
    },
    showDistribution() {
      return this.metrics.totalResponseCount && !this.ratingFilterEnabled;
    },
    responseCount() {
      return this.metrics.totalResponseCount
        ? this.metrics.totalResponseCount.toLocaleString()
        : '--';
    },
    satisfactionScoreDisplay() {
      if (this.hasMixedScales || !this.singleScale) return '--';
      return this.scaleScore(this.singleScale);
    },
    satisfactionScoreTooltip() {
      if (this.hasMixedScales) {
        return this.$t('CSAT_REPORTS.METRIC.SATISFACTION_SCORE.MIXED_SCALES');
      }
      if (this.singleScale && !this.isDefaultScale(this.singleScale)) {
        return this.$t('CSAT_REPORTS.METRIC.SATISFACTION_SCORE.OTHER_SCALE');
      }
      return this.$t('CSAT_REPORTS.METRIC.SATISFACTION_SCORE.TOOLTIP');
    },
    responseRateDisplay() {
      return formatMetricWithCount(
        this.metrics.totalResponseCount,
        this.metrics.totalSentMessagesCount
      );
    },
  },
  methods: {
    formatMetric: formatMetricWithCount,
    isDefaultScale(scale) {
      return scale.scale_min === 1 && scale.scale_max === 5;
    },
    // 1 a 5: "77,8% · 7/9", como sempre foi. Outra escala: "8,1/10".
    scaleScore(scale) {
      if (this.isDefaultScale(scale)) {
        const positive =
          (scale.ratings_count[4] || 0) + (scale.ratings_count[5] || 0);
        return formatMetricWithCount(positive, scale.count);
      }
      const { main, unit } = formatCsatScore(
        { kind: scale.score_kind, value: scale.score, max: scale.scale_max },
        this.$t
      );
      return `${main}${unit}`;
    },
    scaleHint(scale) {
      return formatCsatScore(
        { kind: scale.score_kind, value: scale.score, max: scale.scale_max },
        this.$t
      ).hint;
    },
  },
};
</script>

<!-- eslint-disable vue/no-unused-refs -->
<!-- Added ref for writing specs -->
<template>
  <div
    class="flex-col lg:flex-row flex flex-wrap mx-0 shadow outline-1 outline outline-n-container rounded-xl bg-n-solid-2 px-6 py-8 gap-4"
  >
    <CsatMetricCard
      :label="$t('CSAT_REPORTS.METRIC.TOTAL_RESPONSES.LABEL')"
      :info-text="$t('CSAT_REPORTS.METRIC.TOTAL_RESPONSES.TOOLTIP')"
      :value="responseCount"
      class="xs:w-full sm:max-w-[50%] lg:w-1/6 lg:max-w-[16%]"
    />
    <CsatMetricCard
      :disabled="ratingFilterEnabled"
      :label="$t('CSAT_REPORTS.METRIC.SATISFACTION_SCORE.LABEL')"
      :info-text="satisfactionScoreTooltip"
      :value="ratingFilterEnabled ? '--' : satisfactionScoreDisplay"
      class="xs:w-full sm:max-w-[50%] lg:w-1/6 lg:max-w-[16%]"
    />
    <CsatMetricCard
      :label="$t('CSAT_REPORTS.METRIC.RESPONSE_RATE.LABEL')"
      :info-text="$t('CSAT_REPORTS.METRIC.RESPONSE_RATE.TOOLTIP')"
      :value="responseRateDisplay"
      class="xs:w-full sm:max-w-[50%] lg:w-1/6 lg:max-w-[16%]"
    />

    <div
      v-if="showDistribution && singleScale"
      ref="csatBarChart"
      class="w-full md:w-1/2 md:max-w-[50%] flex-1"
    >
      <ScaleDistributionBar :scale="singleScale" />
    </div>

    <div
      v-if="showDistribution && hasMixedScales"
      ref="csatScaleRows"
      class="flex flex-col w-full gap-4 pt-4 border-t border-n-weak"
    >
      <div
        v-for="scale in scales"
        :key="`${scale.scale_min}-${scale.scale_max}`"
        class="flex flex-col gap-3 lg:flex-row lg:items-center"
      >
        <div class="flex flex-col gap-0.5 lg:w-1/4 shrink-0">
          <span class="text-sm font-medium text-n-slate-12">
            {{
              $t('CSAT_REPORTS.METRIC.SCALE_ROW.TITLE', {
                scale: scale.scale_label,
              })
            }}
          </span>
          <span class="text-xs text-n-slate-11">
            {{
              $t('CSAT_REPORTS.METRIC.SCALE_ROW.RESPONSES', scale.count, {
                count: scale.count,
              })
            }}
          </span>
        </div>
        <div class="flex flex-col lg:w-1/6 shrink-0">
          <span class="text-lg font-medium tabular-nums text-n-slate-12">
            {{ scaleScore(scale) }}
          </span>
          <span class="text-xs text-n-slate-11">{{ scaleHint(scale) }}</span>
        </div>
        <ScaleDistributionBar :scale="scale" class="flex-1" />
      </div>
    </div>
  </div>
</template>
